import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatDate, statusColor, waLink } from "@/lib/utils";
import { toast } from "sonner";
import { Phone, MessageSquare, Play, CheckCircle2, MapPin } from "lucide-react";

function DriverTripCard({ tr, onUpdate, t }) {
  const key = `${tr.code}-${tr.trip_index}`;
  const update = async (status) => {
    try { await api.patch(`/bookings/${tr.booking_id}/trips/${tr.trip_index}/status`, { status }); toast.success(t("saved")); onUpdate(); }
    catch { toast.error(t("failed")); }
  };
  return (
    <Card data-testid={`drv-trip-${key}`}>
      <CardContent className="p-5 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div><div className="text-xs uppercase text-slate-500 tracking-widest">{tr.code} · Trip {tr.trip_index + 1}/{tr.trip_count}</div><div className="font-bold text-lg">{tr.route}</div></div>
          <Badge className={statusColor(tr.trip_status)}>{t(`st_${tr.trip_status}`)}</Badge>
        </div>
        <div className="grid sm:grid-cols-3 gap-3 text-sm">
          <div><div className="text-xs text-slate-500 uppercase">{t("pickup_date")}</div><div className="font-semibold">{formatDate(tr.pickup_date)}</div></div>
          <div><div className="text-xs text-slate-500 uppercase">{t("vehicle_type")}</div><div className="font-semibold">{tr.vehicle_plate} · {tr.vehicle_model}</div></div>
          <div><div className="text-xs text-slate-500 uppercase">{t("passengers")}</div><div className="font-semibold">{tr.passengers} pax</div></div>
        </div>
        {tr.pickup_location && <div className="text-sm flex items-center gap-1 text-slate-700"><MapPin className="w-4 h-4 text-emerald-600" />{tr.pickup_location}</div>}
        {tr.notes && <div className="text-sm text-slate-500">{t("notes")}: {tr.notes}</div>}
        <div className="rounded-xl bg-slate-50 p-3 flex flex-wrap items-center justify-between gap-2">
          <div><div className="font-semibold">{tr.customer_name}</div><div className="text-sm text-slate-500">{tr.customer_phone}</div></div>
          <div className="flex gap-2">
            <a href={`tel:${tr.customer_phone}`}><Button size="sm" variant="outline" data-testid={`call-${key}`}><Phone className="w-4 h-4" /></Button></a>
            <a href={waLink(tr.customer_phone, `Assalamu'alaikum ${tr.customer_name}, saya driver Abdan Pro untuk perjalanan ${tr.route} (${tr.code}). Saya akan menjemput sesuai jadwal.`)} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" data-testid={`wa-${key}`}><MessageSquare className="w-4 h-4" /></Button></a>
          </div>
        </div>
        <div className="flex gap-2">
          {tr.trip_status === "dispatched" && <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => update("in-progress")} data-testid={`start-${key}`}><Play className="w-4 h-4 mr-1" />{t("start_trip")}</Button>}
          {tr.trip_status === "in-progress" && <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => update("completed")} data-testid={`done-${key}`}><CheckCircle2 className="w-4 h-4 mr-1" />{t("complete_trip")}</Button>}
        </div>
      </CardContent>
    </Card>
  );
}

export function DriverToday() {
  const { t } = useApp();
  const [trips, setTrips] = useState([]);
  const load = () => api.get("/driver/trips").then((r) => setTrips(r.data.items));
  useEffect(() => { load(); }, []);
  const today = new Date().toISOString().slice(0, 10);
  const active = trips.filter((tr) => tr.trip_status !== "completed");
  const todayTrips = active.filter((tr) => tr.pickup_date.slice(0, 10) <= today);
  const upcoming = active.filter((tr) => tr.pickup_date.slice(0, 10) > today);
  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">{t("today_trip")}</h1>
      {active.length === 0 && <Card><CardContent className="p-8 text-center text-slate-500" data-testid="drv-empty">{t("no_trips_assigned")}</CardContent></Card>}
      {todayTrips.map((tr) => <DriverTripCard key={`${tr.booking_id}-${tr.trip_index}`} tr={tr} onUpdate={load} t={t} />)}
      {upcoming.length > 0 && <><h2 className="text-lg font-semibold text-slate-600">{t("upcoming")}</h2>
        {upcoming.map((tr) => <DriverTripCard key={`${tr.booking_id}-${tr.trip_index}`} tr={tr} onUpdate={load} t={t} />)}</>}
    </div>
  );
}

export function DriverHistory() {
  const { t } = useApp();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/driver/trips").then((r) => setRows(r.data.items.filter((tr) => tr.trip_status === "completed"))); }, []);
  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-bold">{t("trip_history")}</h1>
      {rows.length === 0 && <Card><CardContent className="p-8 text-center text-slate-500">{t("no_data")}</CardContent></Card>}
      {rows.map((tr) => (
        <Card key={`${tr.booking_id}-${tr.trip_index}`} data-testid={`hist-${tr.code}-${tr.trip_index}`}><CardContent className="p-4 flex items-center justify-between">
          <div><div className="font-mono font-bold">{tr.code}</div><div className="text-sm text-slate-500">{tr.route} · {formatDate(tr.pickup_date)}</div></div>
          <Badge className={statusColor("completed")}>{t("st_completed")}</Badge>
        </CardContent></Card>))}
    </div>
  );
}
